import { hasVisitedParam } from './queryParamTracker';
import type { FetchCartOutput } from './fetchCart';
import type { GwpVariant, GwpProduct, GwpCollection, GwpPage, GwpState, SelectedGift } from './types';

export const GiftState = {
  UNAVAILABLE: 'UNAVAILABLE',
  GROWING: 'GROWING',
  ELIGIBLE: 'ELIGIBLE',
  CLAIMED: 'CLAIMED'
} as const;

type GiftStateType = typeof GiftState[keyof typeof GiftState];

/**
 * Extracts a gating key/value from tier's linkToEnable.value in the form "key=value".
 * Accepts an optional leading '?' (e.g., '?key=value'). If multiple pairs exist, the first is used.
 * Returns null when absent or malformed.
 */
function extractGatingPair(obj: FetchCartOutput['freeGiftObjects']['edges'][0]['node']): { key: string; value: string } | null {
  const raw = typeof obj?.linkToEnable?.value === 'string' ? obj.linkToEnable.value.trim() : '';
  if (!raw) {
    return null;
  }
  const qs = raw.startsWith('?') ? raw.slice(1) : raw;
  if (!qs.includes('=')) {
    return null;
  }
  const firstPair = qs.split('&')[0];
  const [k, v] = firstPair.split('=');
  const key = (k || '').trim();
  const value = (v || '').trim();
  if (!key || !value) {
    return null;
  }
  return { key, value };
}

/**
 * Returns true if the tier is enabled based on the user's visited query params.
 * If no gating pair is present, returns true (tier enabled).
 */
function isTierEnabledByVisitedParams(obj: FetchCartOutput['freeGiftObjects']['edges'][0]['node']): boolean {
  const pair = extractGatingPair(obj);
  if (!pair) {
    return true;
  }
  const result = hasVisitedParam({ key: pair.key, value: pair.value });
  return result;
}

/**
 * Compute the GWP state from cart + free gift metaobjects.
 */
export function computeGwp({ cart, freeGiftObjects }: {
  cart: FetchCartOutput['cart'] | null;
  freeGiftObjects: FetchCartOutput['freeGiftObjects'] | null;
}): {
  requiredSpend: number;
  currentSpend: number;
  giftState: GiftStateType;
  type: string;
  collections: GwpCollection[];
  page: GwpPage | null;
  giftProducts: GwpProduct[];
  selectedGift: SelectedGift | null;
  title?: string;
  description?: string;
  shouldRemoveGift?: boolean;
  replacementGift?: {
    variantId: string;
    productTitle: string;
    vendor: string;
    price: number;
  } | null;
} {
  const fallback = {
    requiredSpend: 0,
    currentSpend: 0,
    giftState: GiftState.UNAVAILABLE as GiftStateType,
    type: '',
    collections: [],
    page: null,
    giftProducts: [],
    selectedGift: null
  };

  if (!cart || !freeGiftObjects) {
    return fallback;
  }

  // Build a flattened list of free gift metaobjects.
  const freeGiftNodes = (freeGiftObjects?.edges || []).map((e) => e?.node).filter(Boolean);

  // Filter tiers by linkToEnable gating using visited query params.
  const eligibleGiftNodes = freeGiftNodes.filter((obj) => isTierEnabledByVisitedParams(obj));

  if (eligibleGiftNodes.length === 0) {
    console.log('No eligible gift nodes, falling back.');
    return fallback;
  }

  // Sort promotions by order (descending)
  const sortedPromotions = [...eligibleGiftNodes].sort((a, b) => {
    const orderA = toNumber(a?.order?.value);
    const orderB = toNumber(b?.order?.value);
    return orderB - orderA; // highest order first
  });

  // Discover vendor names in the cart (excluding free gifts).
  const vendorNamesInCart = new Set(
    (cart?.lines?.edges || [])
      .map((edge) => edge?.node)
      .filter((node) => {
        const isFreeGift =
          Array.isArray(node?.attributes) &&
          node.attributes.some((a) => a?.key === '_freeGift' || a?.key === '_promotional');
        return !isFreeGift;
      })
      .map((node) => node?.variant?.vendor?.reference?.title?.value)
      .filter(Boolean)
  );

  const vendorNames = Array.from(vendorNamesInCart.values());

  // Check if there's currently a free gift in cart
  const freeGiftLine = (cart?.lines?.edges || [])
    .map((e) => e?.node)
    .find(
      (n) =>
        Array.isArray(n?.attributes) && n.attributes.some((a) => a?.key === '_freeGift' || a?.key === '_promotional')
    );

  const selectedGift: SelectedGift | null = freeGiftLine ? {
    lineId: String(freeGiftLine?.id || ''),
    variantId: String(freeGiftLine?.variant?.id || ''),
    productId: String(freeGiftLine?.variant?.product?.id || ''),
    title: String(freeGiftLine?.variant?.product?.title || '')
  } : null;

  // If there's a selected gift, validate it still matches current cart vendors
  if (selectedGift) {
    // Find which promotion this gift belongs to by checking all promotions
    let giftPromotionVendors: string[] = [];
    let originalGiftProductTitle: string = '';
    
    for (const promo of sortedPromotions) {
      const promotionProducts = promo?.products?.references?.nodes || [];
      const hasMatchingProduct = promotionProducts.some(
        (product) => String(product?.id) === selectedGift.productId
      );
      
      if (hasMatchingProduct) {
        giftPromotionVendors = promo?.vendors?.references?.nodes?.map(
          (vendor) => vendor?.internalName?.value
        ).filter(Boolean) || [];
        
        // Store the original gift product title
        const matchedProduct = promotionProducts.find(
          (product) => String(product?.id) === selectedGift.productId
        );
        originalGiftProductTitle = matchedProduct?.title || '';
        break;
      }
    }

    // Check if current cart vendors intersect with gift's promotion vendors
    const hasVendorMatch = vendorNames.some((cartVendorName) => 
      giftPromotionVendors.includes(String(cartVendorName))
    );

    // If no vendor match, flag for removal and find replacement
    if (!hasVendorMatch && giftPromotionVendors.length > 0) {
      console.log('[GWP] Gift vendor mismatch detected. Finding replacement gift...', {
        cartVendors: vendorNames,
        giftPromotionVendors,
        selectedGift,
        originalProductTitle: originalGiftProductTitle
      });
      
      // Find replacement gift from next eligible promotion with same product title
      let replacementGift: { variantId: string; productTitle: string; vendor: string; price: number; } | null = null;
      
      for (const promo of sortedPromotions) {
        const promotionVendors = promo?.vendors?.references?.nodes?.map(
          (vendor) => vendor?.internalName?.value
        ) || [];
        
        // Check if this promotion matches current cart vendors
        const hasIntersection = vendorNames.some((cartVendorName) => 
          promotionVendors.includes(String(cartVendorName))
        );
        
        if (!hasIntersection) continue;
        
        // Check if this promotion is GWP type
        const isGwpType = promo?.type?.value === 'Spend X, choose a product (GWP)';
        if (!isGwpType) continue;
        
        // Look for matching product title in this promotion
        const promotionProducts = promo?.products?.references?.nodes || [];
        
        for (const product of promotionProducts) {
          if (String(product?.title || '') === originalGiftProductTitle) {
            // Found matching product, get first available variant
            const availableVariant = product?.variants?.nodes?.find(v => v?.availableForSale);
            
            if (availableVariant) {
              const vendor = String(availableVariant?.vendor?.reference?.title?.value || '');
              replacementGift = {
                variantId: String(availableVariant?.id || ''),
                productTitle: String(product?.title || ''),
                vendor: vendor,
                price: toNumber(availableVariant?.price?.amount)
              };
              
              console.log('[GWP] Found replacement gift:', replacementGift);
              break;
            }
          }
        }
        
        if (replacementGift) break;
      }
      
      return {
        ...fallback,
        selectedGift,
        shouldRemoveGift: true,
        replacementGift,
        type: 'Spend X, choose a product (GWP)'
      };
    }
  }

  // Find first promotion that intersects with cart vendors
  let selectedPromotion: FetchCartOutput['freeGiftObjects']['edges'][0]['node'] | null = null;

  for (const promo of sortedPromotions) {
    const promotionVendors = promo?.vendors?.references?.nodes?.map((vendor) => vendor?.internalName?.value) || [];

    const hasIntersection = vendorNames.some((cartVendorName) => promotionVendors.includes(String(cartVendorName)));

    if (hasIntersection) {
      selectedPromotion = promo;
      break;
    }
  }

  const promotionType = selectedPromotion?.type?.value || '';

  // Get price tier scope collections if they exist
  const priceTierScopeCollections = selectedPromotion?.priceTierScope?.references?.nodes || [];

  const scopedProductIds = new Set(
    priceTierScopeCollections.flatMap((collection) => (collection?.products?.nodes || []).map((p) => String(p?.id)))
  );

  /**
   * NEW: gather product IDs to *exclude* (ignore spend)
   * These come from selectedPromotion?.products?.references?.nodes
   */
  const ignoredProductIds = new Set((selectedPromotion?.products?.references?.nodes || []).map((p) => String(p?.id)));

  // Calculate current spend with ignore support
  const currentSpend = calculateCurrentSpend(cart, scopedProductIds, ignoredProductIds);

  // Parse required spend from priceTier
  const requiredSpend = parsePriceTier(selectedPromotion?.priceTier?.value ?? 0.01);

  // If none match vendor criteria, fallback
  if (!selectedPromotion) {
    return {
      ...fallback,
      type: 'Spend X, choose a product (GWP)',
      requiredSpend,
      selectedGift
    };
  }

  // If spend is insufficient for the gift already in cart, flag for removal
  if (selectedGift && requiredSpend > 0 && currentSpend < requiredSpend) {
    console.log('[GWP] Gift spend threshold no longer met. Flagging for removal.', {
      currentSpend,
      requiredSpend,
      selectedGift
    });
    return {
      ...fallback,
      selectedGift,
      shouldRemoveGift: true,
      type: promotionType || 'Spend X, choose a product (GWP)'
    };
  }

  const title = selectedPromotion?.title?.value;
  const description = selectedPromotion?.description?.value;

  // Process based on promotion type
  switch (promotionType) {
    case 'Spend X, choose a product (GWP)':
      return processGwpPromotion({
        selectedPromotion,
        currentSpend,
        requiredSpend,
        selectedGift,
        promotionType,
        title,
        description
      });

    case 'Spend X, see collection link(s)':
      return processCollectionPromotion({
        selectedPromotion,
        currentSpend,
        requiredSpend,
        cart,
        promotionType,
        title,
        description
      });

    case 'Spend X, see message':
      return processPagePromotion({
        selectedPromotion,
        currentSpend,
        requiredSpend,
        promotionType,
        title,
        description
      });

    default:
      return {
        ...fallback,
        selectedGift,
        currentSpend,
        type: promotionType
      };
  }
}

/**
 * Calculate current spend based on cart and optional scoped product IDs.
 * Also ignores spend from any product IDs passed in ignoredProductIds.
 */
function calculateCurrentSpend(
  cart: FetchCartOutput['cart'], 
  scopedProductIds: Set<string>, 
  ignoredProductIds: Set<string> = new Set()
): number {
  const lines = (cart?.lines?.edges || []).map((e) => e?.node);
  let total = 0;

  // If no scope, start from cart subtotal, otherwise start from 0
  if (scopedProductIds.size === 0) {
    total = toNumber(cart?.cost?.subtotalAmount?.amount);
  }

  for (const line of lines) {
    // Skip free gift lines
    const isFreeGift =
      Array.isArray(line?.attributes) &&
      line.attributes.some((a) => a?.key === '_freeGift' || a?.key === '_promotional');
    if (isFreeGift) continue;

    const productId = String(line?.variant?.product?.id || '');
    const quantity = toNumber(line?.quantity);
    const price = toNumber(line?.variant?.price?.amount);
    const lineTotal = quantity * price;

    // Always subtract ignored products
    if (ignoredProductIds.has(productId)) {
      total -= lineTotal;
      continue;
    }

    // If scoped, only add products in scope
    if (scopedProductIds.size > 0) {
      if (scopedProductIds.has(productId)) {
        total += lineTotal;
      }
    }
    // If no scope, ignored products already subtracted above
  }

  return total;
}

/**
 * Process GWP (Gift with Purchase) promotion type.
 */
function processGwpPromotion({
  selectedPromotion,
  currentSpend,
  requiredSpend,
  selectedGift,
  promotionType,
  title,
  description
}: {
  selectedPromotion: FetchCartOutput['freeGiftObjects']['edges'][0]['node'];
  currentSpend: number;
  requiredSpend: number;
  selectedGift: SelectedGift | null;
  promotionType: string;
  title: string | undefined;
  description: string | undefined;
}): GwpState {
  const productNodes = selectedPromotion?.products?.references?.nodes || [];

  const giftProducts: GwpProduct[] = [];
  for (const product of productNodes) {
    if (product.variants.nodes.every((v) => !v.availableForSale)) {
      continue;
    }

    const variants: GwpVariant[] = (product?.variants?.nodes || []).map((v) => ({
      availableForSale: !!v?.availableForSale,
      id: String(v?.id ?? ''),
      price: toNumber(v?.price?.amount),
      currencyCode: v?.price?.currencyCode,
      compareAtPrice: toNumber(v?.compareAtPrice?.amount),
      compareAtCurrencyCode: v?.compareAtPrice?.currencyCode,
      title: String(v?.title ?? ''),
      vendor: String(v?.vendor?.reference?.title?.value ?? ''),
      image: v?.image?.url
    }));

    const anyAvailable = variants.some((v) => v.availableForSale);

    const giftProduct: GwpProduct = {
      id: String(product?.id ?? ''),
      image: product?.featuredImage?.url ?? null,
      title: String(product?.title ?? ''),
      status: anyAvailable ? 'ACTIVE' : 'UNAVAILABLE',
      descriptionHtml: product?.descriptionHtml ?? null,
      onlineStoreUrl: product?.onlineStoreUrl ?? null,
      variants
    };
    giftProducts.push(giftProduct);
  }

  // If no gift products are available, return unavailable state
  if (giftProducts.length === 0) {
    return {
      requiredSpend,
      currentSpend,
      giftState: selectedGift ? GiftState.CLAIMED : GiftState.UNAVAILABLE,
      type: promotionType,
      collections: [],
      page: null,
      giftProducts: [],
      selectedGift,
      title: title ?? '',
      description: description ?? ''
    };
  }

  // Determine gift state
  let giftState: GiftStateType = GiftState.UNAVAILABLE;
  if (selectedGift) {
    giftState = GiftState.CLAIMED;
  } else if (requiredSpend > 0 && currentSpend >= requiredSpend) {
    giftState = GiftState.ELIGIBLE;
  } else if (currentSpend > 0) {
    giftState = GiftState.GROWING;
  }

  return {
    requiredSpend,
    currentSpend,
    giftState,
    type: promotionType,
    collections: [],
    page: null,
    giftProducts,
    selectedGift,
    title: title ?? '',
    description: description ?? ''
  };
}

/**
 * Process Collection promotion type.
 */
function processCollectionPromotion({
  selectedPromotion,
  currentSpend,
  requiredSpend,
  cart,
  promotionType,
  title,
  description
}: {
  selectedPromotion: FetchCartOutput['freeGiftObjects']['edges'][0]['node'];
  currentSpend: number;
  requiredSpend: number;
  cart: FetchCartOutput['cart'];
  promotionType: string;
  title: string | undefined;
  description: string | undefined;
}): GwpState {
  const collectionNodes = selectedPromotion?.suggestedCollections?.references?.nodes || [];

  const collections: GwpCollection[] = collectionNodes.map((collection) => ({
    id: String(collection?.id ?? ''),
    title: String(collection?.title ?? ''),
    handle: String(collection?.handle ?? ''),
    image: collection?.image?.url ?? null
  }));

  // Check if any product from suggested collections is in the cart
  const collectionProductIds = new Set(
    collectionNodes.flatMap((collection) => (collection?.products?.nodes || []).map((p) => String(p?.id)))
  );

  const cartLines = (cart?.lines?.edges || []).map((e) => e?.node);
  const hasCollectionProduct = cartLines.some((line) => {
    // Skip free gift lines
    const isFreeGift =
      Array.isArray(line?.attributes) &&
      line.attributes.some((a) => a?.key === '_freeGift' || a?.key === '_promotional');
    if (isFreeGift) {
      return false;
    }

    const productId = String(line?.variant?.product?.id || '');
    return collectionProductIds.has(productId);
  });

  // Determine gift state
  // IMPORTANT: Only mark as CLAIMED if spend threshold is met AND has collection product
  let giftState: GiftStateType = GiftState.UNAVAILABLE;
  if (requiredSpend > 0 && currentSpend >= requiredSpend) {
    // Spend threshold met
    if (hasCollectionProduct) {
      giftState = GiftState.CLAIMED;
    } else {
      giftState = GiftState.ELIGIBLE;
    }
  } else if (currentSpend > 0) {
    // Still growing toward threshold
    giftState = GiftState.GROWING;
  }

  return {
    requiredSpend,
    currentSpend,
    giftState,
    type: promotionType,
    collections,
    page: null,
    giftProducts: [],
    selectedGift: null,
    title: title ?? '',
    description: description ?? ''
  };
}

/**
 * Process Page/Message promotion type.
 */
function processPagePromotion({ 
  selectedPromotion, 
  currentSpend, 
  requiredSpend, 
  promotionType, 
  title, 
  description 
}: {
  selectedPromotion: FetchCartOutput['freeGiftObjects']['edges'][0]['node'];
  currentSpend: number;
  requiredSpend: number;
  promotionType: string;
  title: string | undefined;
  description: string | undefined;
}): GwpState {
  const pageRef = selectedPromotion?.suggestedPage?.reference;

  const page: GwpPage | null = pageRef
    ? {
        id: String(pageRef?.id ?? ''),
        handle: String(pageRef?.handle ?? ''),
        title: String(pageRef?.title ?? ''),
        body: String(pageRef?.body ?? ''),
        triggerText: String(pageRef?.triggerText?.value ?? '')
      }
    : null;

  // Determine gift state (no CLAIMED state for page type)
  let giftState: GiftStateType = GiftState.UNAVAILABLE;
  if (requiredSpend > 0 && currentSpend >= requiredSpend) {
    giftState = GiftState.ELIGIBLE;
  } else if (currentSpend > 0) {
    giftState = GiftState.GROWING;
  }

  return {
    requiredSpend,
    currentSpend,
    giftState,
    type: promotionType,
    collections: [],
    page,
    giftProducts: [],
    selectedGift: null,
    title: title ?? '',
    description: description ?? ''
  };
}

/**
 * Parse a price tier field which may be a number-like string or a JSON string.
 */
function parsePriceTier(value: string | number | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    // Try JSON structure: { "amount": "100.0", "currency_code": "GBP" }.
    const trimmed = value.trim();

    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        const amt = parsed?.amount;
        return toNumber(amt);
      } catch (e) {
        console.warn('[GWP] Failed to parse price tier', value, e);
        // Fall through to numeric parse if JSON fails.
      }
    }
    return toNumber(value);
  }
  return 0;
}

/**
 * Convert a value to finite number, supporting numeric strings.
 */
function toNumber(v: string | number | null | undefined): number {
  const n = typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(n) ? n : 0;
}

/**
 * Returns true if two sets intersect.
 */
export function intersects(a: Set<string>, b: Set<string>): boolean {
  for (const v of a) {
    if (b.has(v)) {
      return true;
    }
  }
  return false;
}